import { state, api, $, $$, toast, escHtml, truncate, customConfirm, formatDateTimeRO } from './core';
import type { RandErrorCodes, RandParts, RandPartsPhotos, RandSacmiLinks } from './tipuri-baza';

/** /api/parts (routes/other.py): rândul din `parts` + `uzura_dovedita` (1 = piesa e pe o lucrare de înlocuire) */
interface PiesaLista extends RandParts { uzura_dovedita: number; }
/** ce a citit AI-ul pe poză (routes/chat.py:api_identify_part) — JSON-ul scris de model, deci orice câmp poate lipsi */
interface AnalizaPoza { description?: string; part_codes?: string[]; manufacturer?: string; text_found?: string; }
/** /api/parts_photos: `ai_result` vine deja despachetat și cu `url` adăugat. Un JSON care nu se poate citi rămâne
 *  text — atunci `.description`/`.part_codes` sunt undefined și eticheta cade pe numele fișierului */
interface PozaPiesa extends Pick<RandPartsPhotos, 'id' | 'filename' | 'machine_id' | 'parts_found' | 'created_at'> {
    ai_result: AnalizaPoza | null; url: string;
}
/** /api/extract-procedures (routes/documents/extracte.py): textele lipsă vin ca '', pașii cu coloanele redenumite */
interface ProceduraLista {
    id: number; machine_id: string; document_id: number | null; document_filename: string; extract_type: string;
    title_ro: string; content_ro: string; tools: string; safety_warnings: string; reference_values: string;
    video_ref: string; start_page: number | null; end_page: number | null;
    steps: { instruction: string; duration_min: number | null; is_checkpoint: number | null; notes: string }[];
}

/* ═══ CĂUTARE PIESE ════════════════════════════════════════════ */
export let partsInited = false;
export let _arStream: MediaStream | null = null;

export async function initParts() {
    if (!partsInited) {
        await _ensureMachineCache();
        // Câte piese are catalogul, din bază. Textul din HTML era scris de mână
        // în aprilie („1148 piese din manualele mașinilor") și rămăsese acolo cât
        // timp catalogul a crescut la 23.937 — mecanicul care nu-și găsea piesa
        // credea că nu e în catalog (audit, capitolul de date greșite).
        (async () => {
            const el = document.getElementById('parts-empty-count');
            if (!el) return;
            try {
                const st = await api('/api/stats');
                if (st && st.parts) {
                    el.textContent = `${Number(st.parts).toLocaleString('ro-RO')} piese din manualele mașinilor`;
                }
            } catch (_e) { /* rămâne textul neutru */ }
        })();
        const [machines, categories] = await Promise.all([
            api('/api/parts/machines'),
            api('/api/parts/categories')
        ]);

        // Catalog Piese: line → machine
        const mSel = $('#parts-machine');
        mSel!.innerHTML = '<option value="">Toate mașinile</option>';
        machines.forEach((m: string) => { mSel!.innerHTML += `<option value="${m}">${m}</option>`; });
        _insertLineSelectBefore(mSel, 'parts-line', 'Toate liniile', 'Toate mașinile');

        const cSel = $('#parts-category');
        cSel!.innerHTML = '<option value="">Toate categoriile</option>';
        categories.forEach((c: string) => { cSel!.innerHTML += `<option value="${c}">${c}</option>`; });

        // Identificare Piesă: line → machine
        const arMSel = document.getElementById('ar-machine');
        if (arMSel) {
            arMSel.innerHTML = '<option value="">Toate mașinile</option>';
            machines.forEach((m: string) => { arMSel.innerHTML += `<option value="${m}">${m}</option>`; });
            _insertLineSelectBefore(arMSel, 'ar-line', 'Toate liniile', 'Toate mașinile');
        }
        // Scheme & Desene: line → machine
        const schMSel = document.getElementById('schemes-machine');
        if (schMSel) {
            schMSel.innerHTML = '<option value="">Selectează mașina...</option>';
            machines.forEach((m: string) => { schMSel.innerHTML += `<option value="${m}">${m}</option>`; });
            _insertLineSelectBefore(schMSel, 'schemes-line', 'Toate liniile', 'Selectează mașina...');
        }

        // Catalog tab buttons
        $('#parts-btn')!.addEventListener('click', () => { state.partsPage = 1; loadParts(); });
        $('#parts-search')!.addEventListener('keydown', e => {
            if (e.key === 'Enter') { state.partsPage = 1; loadParts(); }
        });
        document.getElementById('parts-reset')?.addEventListener('click', () => {
            ($('#parts-search') as HTMLInputElement).value = '';
            const partsLine = document.getElementById('parts-line');
            if (partsLine) { (partsLine as HTMLInputElement).value = ''; partsLine.dispatchEvent(new Event('change')); }
            if ($('#parts-machine')) ($('#parts-machine') as HTMLInputElement).value = '';
            if ($('#parts-category')) ($('#parts-category') as HTMLInputElement).value = '';
            if ($('#parts-wear')) ($('#parts-wear') as HTMLInputElement).value = '';
            state.partsPage = 1;
            const emptyState = document.getElementById('parts-empty-state');
            const tableWrap = document.getElementById('parts-table-wrap');
            if (emptyState) emptyState.style.display = '';
            if (tableWrap) tableWrap.style.display = 'none';
            $('#parts-info')!.textContent = '';
            $('#parts-table tbody')!.innerHTML = '';
            $('#parts-pagination')!.innerHTML = '';
        });

        // Tab switching
        document.querySelectorAll('#parts-tabs .dx-tab').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('#parts-tabs .dx-tab').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                document.querySelectorAll('#sec-parts .dx-panel').forEach(p => p.classList.remove('active'));
                const target = document.getElementById((btn as HTMLElement).dataset.tab!);
                if (target) target.classList.add('active');
                if ((btn as HTMLElement).dataset.tab !== 'parts-tab-identify') _stopArCamera();
            });
        });

        // Detect device capabilities
        const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        const isSecure = location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
        const isMobile = /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent);
        const useLiveCam = hasGetUserMedia && isSecure;

        const liveCamDiv = document.getElementById('ar-live-cam');
        const fileCamDiv = document.getElementById('ar-file-cam');
        const dropZoneDiv = document.getElementById('ar-drop-zone');

        if (isMobile) {
            // Mobile: show file input with capture (opens native camera)
            if (liveCamDiv) liveCamDiv.style.display = useLiveCam ? '' : 'none';
            if (fileCamDiv) fileCamDiv.style.display = useLiveCam ? 'none' : '';
            if (dropZoneDiv) dropZoneDiv.style.display = 'none';
        } else {
            // Desktop: show drag & drop + file picker, optionally live cam
            if (liveCamDiv) liveCamDiv.style.display = useLiveCam ? '' : 'none';
            if (fileCamDiv) fileCamDiv.style.display = 'none';
            if (dropZoneDiv) dropZoneDiv.style.display = '';
        }

        // Live camera controls
        document.getElementById('ar-start-btn')?.addEventListener('click', _startArCamera);
        document.getElementById('ar-stop-btn')?.addEventListener('click', _stopArCamera);
        document.getElementById('ar-capture-btn')?.addEventListener('click', _captureArPhoto);

        // Mobile file input
        document.getElementById('ar-file-input')?.addEventListener('change', _handleArFileInput);

        // Desktop file input
        document.getElementById('ar-desktop-file-input')?.addEventListener('change', _handleArFileInput);

        // Desktop drag & drop
        if (dropZoneDiv) {
            dropZoneDiv.addEventListener('dragover', (e) => {
                e.preventDefault(); e.stopPropagation();
                dropZoneDiv.classList.add('dragover');
            });
            dropZoneDiv.addEventListener('dragleave', (e) => {
                e.preventDefault(); e.stopPropagation();
                dropZoneDiv.classList.remove('dragover');
            });
            dropZoneDiv.addEventListener('drop', (e) => {
                e.preventDefault(); e.stopPropagation();
                dropZoneDiv.classList.remove('dragover');
                const file = e.dataTransfer!.files && e.dataTransfer!.files[0];
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (ev) => _resizeAndIdentify(ev.target!.result);
                    reader.readAsDataURL(file);
                }
            });
        }

        // Identify search
        document.getElementById('ar-search-btn')?.addEventListener('click', () => _searchIdentifyPart());
        document.getElementById('ar-search')?.addEventListener('keydown', e => {
            if (e.key === 'Enter') _searchIdentifyPart();
        });

        // Schemes
        document.getElementById('schemes-btn')?.addEventListener('click', () => { _schemesPage=1; _schemesTypeFilter=''; _loadSchemes(); });
        document.getElementById('schemes-search')?.addEventListener('keydown', e => {
            if (e.key === 'Enter') { _schemesPage=1; _schemesTypeFilter=''; _loadSchemes(); }
        });

        // Load photos gallery
        _loadPartsPhotosGallery();

        partsInited = true;
    }
}

export async function loadParts() {
    const q = ($('#parts-search') as HTMLInputElement).value;
    const machine = ($('#parts-machine') as HTMLInputElement).value;
    const category = ($('#parts-category') as HTMLInputElement).value;
    const wear = ($('#parts-wear') as HTMLInputElement).value;

    const emptyState = document.getElementById('parts-empty-state');
    const tableWrap = document.getElementById('parts-table-wrap');
    if (emptyState) emptyState.style.display = 'none';
    if (tableWrap) tableWrap.style.display = '';

    const params = new URLSearchParams({ page: String(state.partsPage), per_page: '50' });
    if (q) params.set('q', q);
    if (machine) params.set('machine_id', machine);
    // 5 sep 2026: linia aleasă chiar filtrează lista, nu doar select-ul vecin
    // (aceeași scăpare pe care a găsit-o Dan în Rapoarte).
    else {
        const linie = (document.getElementById('parts-line') as HTMLSelectElement | null)?.value || '';
        if (linie) params.set('line_id', linie);
    }
    if (category) params.set('category', category);
    if (wear) params.set('wear_type', wear);

    const result: { data: PiesaLista[]; total: number; page: number; per_page: number } = await api(`/api/parts?${params}`);
    $('#parts-info')!.textContent = `${result.total} piese găsite (pagina ${result.page} din ${Math.ceil(result.total / result.per_page) || 1})`;

    const tbody = $('#parts-table tbody');
    tbody!.innerHTML = '';

    if (result.data.length === 0) {
        tbody!.innerHTML = '<tr><td colspan="9" class="empty-state">Nicio piesă găsită</td></tr>';
    } else {
        result.data.forEach((p: PiesaLista) => {
            // Eticheta spune de unde vine: „dovedit" înseamnă că piesa chiar e
            // legată de o mentenanță de înlocuire; restul sunt clasificări făcute
            // de model la extragere, fără nimic în manual — 1.668 din 1.799 de
            // piese marcate „uzură" erau în situația asta (audit 19 aug 2026).
            // vine din `CASE … THEN 1 ELSE 0` (routes/other.py), deci doar 0/1 — comparația cu `true` nu putea fi adevărată
            const _dovedit = p.uzura_dovedita === 1;
            const wearLabel = p.wear_type === 'U'
                ? `<span class="badge badge-red" title="${_dovedit ? 'Piesă de uzură — legată de o mentenanță de înlocuire' : 'Marcată „uzură" la extragerea din manual, fără confirmare. Verifică înainte să comanzi.'}">Uzură${_dovedit ? '' : ' ?'}</span>`
                : p.wear_type === 'S'
                ? '<span class="badge badge-yellow" title="Consumabil — înlocuire periodică">Consumabil</span>'
                : '<span class="badge badge-gray">—</span>';

            const hasDoc = p.document_id && p.document_id > 0;
            const manualBtn = hasDoc
                ? `<a href="#" class="parts-manual-btn" data-part-id="${p.id}" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border-radius:8px;background:rgba(0,180,255,.1);border:1px solid rgba(0,180,255,.3);color:#00b4ff;text-decoration:none;font-size:.78rem;font-weight:600;white-space:nowrap;cursor:pointer" title="Deschide manualul la pagina piesei">📖 PDF</a>`
                : '<span style="color:var(--text-muted);font-size:.75rem">—</span>';

            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td><strong>${escHtml(p.machine_id)}</strong></td>
                <td><code style="color:#fb923c;font-weight:600">${escHtml(p.part_number)}</code></td>
                <td>${escHtml(p.description_ro)}</td>
                <td><span class="badge badge-blue">${escHtml(p.category)}</span></td>
                <td>${wearLabel}</td>
                <td>${escHtml(p.interval_text)}</td>
                <td>${escHtml(p.supplier)}</td>
                <td>${p.quantity_per_machine || 1}</td>
                <td>${manualBtn}</td>
            `;
            tbody!.appendChild(tr);
        });
        tbody!.querySelectorAll('.parts-manual-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                const el = btn as HTMLElement;
                const partId = el.dataset.partId;
                const orig = el.innerHTML;
                el.innerHTML = '⏳...';
                try {
                    const res = await api('/api/parts/' + partId + '/manual_page');
                    if (res.document_id) {
                        // Pagina vine deja 1-based din server (`_pagina_pentru_vizualizator`).
                        // Când codul e prea scurt ca potrivirea să însemne ceva, serverul
                        // spune `source:'nesigur'` — omul trebuie să știe înainte să caute
                        // într-un manual de 466 de pagini (audit 2.21).
                        if (res.source === 'nesigur' && res.avertisment) toast(res.avertisment, 'warning');
                        window.open('/api/documents/' + res.document_id + '/view?page=' + (res.page || 1), '_blank');
                    } else {
                        toast(res.error || 'Nu s-a găsit manual', 'warning');
                    }
                } catch (_e) { toast('Eroare la căutare manual', 'error'); }
                el.innerHTML = orig;
            });
        });
    }

    renderPagination('#parts-pagination', result.total, result.per_page, state.partsPage, (pg: number) => {
        state.partsPage = pg;
        loadParts();
    });
}

/* ── Camera / AR Identify ── */
export async function _startArCamera() {
    try {
        const video = document.getElementById('ar-video');
        _arStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', width: { ideal: 1280 } }
        });
        (video as HTMLVideoElement).srcObject = _arStream;
        document.getElementById('ar-start-btn')!.style.display = 'none';
        document.getElementById('ar-capture-btn')!.style.display = '';
        document.getElementById('ar-stop-btn')!.style.display = '';
    } catch (err) {
        alert('Nu s-a putut accesa camera: ' + (err as Error).message);
    }
}

export function _stopArCamera() {
    if (_arStream) {
        _arStream.getTracks().forEach(t => t.stop());
        _arStream = null;
    }
    const video = document.getElementById('ar-video');
    if (video) (video as HTMLVideoElement).srcObject = null;
    const startBtn = document.getElementById('ar-start-btn');
    const captureBtn = document.getElementById('ar-capture-btn');
    const stopBtn = document.getElementById('ar-stop-btn');
    if (startBtn) startBtn.style.display = '';
    if (captureBtn) captureBtn.style.display = 'none';
    if (stopBtn) stopBtn.style.display = 'none';
}

export function _captureArPhoto() {
    const video = document.getElementById('ar-video') as HTMLVideoElement | null;
    const canvas = document.getElementById('ar-canvas') as HTMLCanvasElement | null;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    _resizeAndIdentify(dataUrl);
}

export function _handleArFileInput(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function(ev) {
        _resizeAndIdentify(ev.target!.result);
    };
    reader.readAsDataURL(file);
}

export function _resizeAndIdentify(dataUrl: string|ArrayBuffer|null) {
    const img = new Image();
    img.onload = function() {
        const MAX = 1024;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d')!.drawImage(img, 0, 0, w, h);
        const compressed = c.toDataURL('image/jpeg', 0.75);
        _showArPreview(compressed);
        _identifyPartFromImage(compressed);
    };
    img.src = dataUrl as string;
}

export function _openLightbox(src: string, caption?: string) {
    const overlay = document.getElementById('lightbox-overlay');
    const img = document.getElementById('lightbox-img');
    const captionEl = document.getElementById('lightbox-caption');
    if (overlay && img) {
        (img as HTMLInputElement).src = src;
        if (captionEl) {
            if (caption) {
                captionEl.innerHTML = caption;
                captionEl.style.display = '';
            } else {
                captionEl.innerHTML = '';
                captionEl.style.display = 'none';
            }
        }
        overlay.style.display = 'flex';
    }
}

export function _attachSourceClickHandlers(container: ParentNode | null) {
    if (!container) return;
    container.querySelectorAll<HTMLElement & { _srcHandlerAttached?: boolean }>('.chat-source-btn').forEach((btn) => {
        if (btn._srcHandlerAttached) return;
        btn._srcHandlerAttached = true;
        btn.addEventListener('click', () => {
            const sType = btn.dataset.type;
            const sId = btn.dataset.id;
            if (sType === 'procedure_steps' || sType === 'document_extracts') {
                openProcedureOverlay(sId!);   // data-id e pus mereu pe buton (diagnostic.ts, chat-ai.ts)
            } else if (sType === 'raw_page') {
                const pg = btn.dataset.page;
                if (sId && pg) window.open(`/api/documents/${sId}/view?page=${pg}`, '_blank');
            } else if (sType === 'maintenance_intervals') {
                // secțiunea „sec-plan" nu există (30 sep 2026: butonul nu făcea nimic);
                // planul de mentenanță se vede în Calendar
                showSection('sec-calendar');
            } else if (sType === 'parts') {
                showSection('sec-parts');
                const si = document.getElementById('parts-search');
                if (si && btn.title) { (si as HTMLInputElement).value = btn.title.substring(0, 30); document.getElementById('parts-btn')?.click(); }
            } else if (sType === 'maintenance_log') {
                showSection('sec-journal');
            }
        });
    });
}

export function _showArPreview(dataUrl: string) {
    const preview = document.getElementById('ar-preview');
    const previewImg = document.getElementById('ar-preview-img');
    if (preview && previewImg) {
        (previewImg as HTMLInputElement).src = dataUrl;
        preview.style.display = '';
    }
}

export function _clearArPreview() {
    const preview = document.getElementById('ar-preview');
    const previewImg = document.getElementById('ar-preview-img') as HTMLImageElement;
    if (preview) preview.style.display = 'none';
    if (previewImg) previewImg.src = '';
    const panel = document.getElementById('ar-result-panel');
    if (panel) panel.innerHTML = '';
}

document.getElementById('ar-preview-clear')?.addEventListener('click', _clearArPreview);

function _urlToDataUrl(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d')!.drawImage(img, 0, 0);
            resolve(c.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = () => reject(new Error('Nu s-a putut încărca imaginea'));
        img.src = url;
    });
}

export async function _identifyPartFromImage(dataUrl: string) {
    if (dataUrl && !dataUrl.startsWith('data:')) {
        try { dataUrl = await _urlToDataUrl(dataUrl); }
        catch { /* will fail at server with better error */ }
    }
    const panel = document.getElementById('ar-result-panel');
    if (!panel) return;
    const machine = (document.getElementById('ar-machine') as HTMLInputElement)?.value || '';

    panel.innerHTML = `<div style="text-align:center;padding:30px 10px">
        <div class="typing-dots"><span></span><span></span><span></span></div>
        <p style="margin-top:10px;font-size:.9rem;color:var(--text-muted)">AI analizează imaginea... (se salvează și pe server)</p>
    </div>`;

    try {
        const resp = await fetch('/api/identify_part', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: dataUrl, machine_id: machine })
        });
        const result = await resp.json();

        if (!resp.ok) {
            panel.innerHTML = `<div class="parts-empty-state" style="padding:20px 10px">
                <p style="color:var(--red)">${escHtml(result.error || 'Eroare la identificare')}</p>
                <p style="margin-top:8px;font-size:.85rem;color:var(--text-muted)">Alternativ, scrieți codul piesei în câmpul de căutare.</p>
            </div>`;
            return;
        }

        const analysis: AnalizaPoza = result.ai_analysis || {};
        const parts: RandParts[] = result.parts_found || [];
        const terms = result.search_terms || [];

        let html = '<div style="margin-bottom:12px">';
        html += '<h4 style="margin:0 0 6px;font-size:.95rem;color:var(--text)">🤖 Rezultat analiză AI</h4>';

        if (analysis.description) {
            html += `<p style="font-size:.9rem;margin:2px 0"><strong>Piesă identificată:</strong> ${escHtml(analysis.description)}</p>`;
        }
        if (analysis.part_codes && analysis.part_codes.length) {
            html += `<p style="font-size:.9rem;margin:2px 0"><strong>Coduri detectate:</strong> ${analysis.part_codes.map((c: string|null|undefined) => `<code style="background:var(--surface);padding:2px 6px;border-radius:4px">${escHtml(c)}</code>`).join(' ')}</p>`;
        }
        if (analysis.manufacturer) {
            html += `<p style="font-size:.9rem;margin:2px 0"><strong>Producător:</strong> ${escHtml(analysis.manufacturer)}</p>`;
        }
        if (analysis.text_found) {
            html += `<p style="font-size:.85rem;margin:4px 0;color:var(--text-muted)"><em>Text citit: ${escHtml(analysis.text_found)}</em></p>`;
        }
        html += '</div>';

        if (parts.length > 0) {
            html += `<h4 style="margin:12px 0 8px;font-size:.95rem;color:var(--text)">🔩 ${parts.length} piese găsite în baza de date</h4>`;
            parts.forEach((p: RandParts) => {
                const wearTxt = p.wear_type === 'U' ? 'Uzură' : p.wear_type === 'S' ? 'Consumabil' : '—';
                html += `<div class="part-card">
                    <h5>${escHtml(p.part_number)} — ${escHtml(p.description_ro || p.description_en || '')}</h5>
                    <p>Mașină: <strong>${escHtml(p.machine_id)}</strong> | Categorie: ${escHtml(p.category || '')} | ${wearTxt}</p>
                    <p>Interval: ${escHtml(p.interval_text || '')} | Furnizor: ${escHtml(p.supplier || '')} | Cant: ${p.quantity_per_machine || 1}</p>
                </div>`;
            });
        } else {
            html += `<div class="parts-empty-state" style="padding:15px 0">
                <p>Nicio piesă din baza de date nu corespunde.</p>
                <p style="font-size:.85rem;color:var(--text-muted);margin-top:4px">Încercați căutarea manuală cu termenii detectați: ${terms.map((t: string|null|undefined) => `"${escHtml(t)}"`).join(', ')}</p>
            </div>`;
            if (terms.length > 0) {
                const searchInput = document.getElementById('ar-search');
                if (searchInput) (searchInput as HTMLInputElement).value = terms[0];
            }
        }

        const identifyQuestion = 'Identificare piesă din imagine' + (machine ? ' (mașina ' + machine + ')' : '');
        const identifyAnswer = [
            analysis.description ? 'Piesă: ' + analysis.description : '',
            analysis.part_codes?.length ? 'Coduri: ' + analysis.part_codes.join(', ') : '',
            analysis.manufacturer ? 'Producător: ' + analysis.manufacturer : '',
            parts.length ? parts.length + ' piese găsite în baza de date' : 'Nicio piesă găsită în BD'
        ].filter(Boolean).join('. ');
        html += _createFeedbackHtml(identifyQuestion, identifyAnswer, 'cloud (vision)', []);

        panel.innerHTML = html;
        _loadPartsPhotosGallery();
    } catch (err) {
        panel.innerHTML = `<div class="parts-empty-state" style="padding:20px">
            <p style="color:var(--red)">Eroare: ${escHtml((err as Error).message)}</p>
            <p style="margin-top:6px;font-size:.85rem;color:var(--text-muted)">Fotografia a fost salvată pe server. Scrieți codul piesei manual în câmpul de căutare.</p>
        </div>`;
        _loadPartsPhotosGallery();
    }
}

let _identifyPage = 1;
const _IDENTIFY_PER_PAGE = 20;

export async function _searchIdentifyPart(page?: number | Event) {
    const q = ((document.getElementById('ar-search') as HTMLInputElement)?.value || '').trim();
    const machine = (document.getElementById('ar-machine') as HTMLInputElement)?.value || '';
    const panel = document.getElementById('ar-result-panel');
    if (!panel) return;

    if (!q && !machine) {
        panel.innerHTML = `<div class="parts-empty-state" style="padding:20px 10px">
            <p>Introduceți un cod, o descriere sau selectați o mașină pentru a căuta piese.</p>
        </div>`;
        return;
    }

    if (typeof page === 'number' && page > 0) _identifyPage = page;
    else _identifyPage = 1;

    panel.innerHTML = '<div style="text-align:center;padding:20px"><div class="typing-dots"><span></span><span></span><span></span></div></div>';

    const params = new URLSearchParams({ per_page: String(_IDENTIFY_PER_PAGE), page: String(_identifyPage) });
    if (q) params.set('q', q);
    if (machine) params.set('machine_id', machine);

    try {
        const result: { data: PiesaLista[]; total: number } = await api(`/api/parts?${params}`);
        if (!result.data || result.data.length === 0) {
            const searchTerm = q ? `„${escHtml(q)}"` : `mașina ${escHtml(machine)}`;
            panel.innerHTML = `<div class="parts-empty-state" style="padding:20px"><p>Nicio piesă găsită pentru ${searchTerm}</p></div>`;
            return;
        }
        const totalPages = Math.ceil(result.total / _IDENTIFY_PER_PAGE);
        let html = `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
            <span style="font-size:.85rem;color:var(--text-muted)">${result.total} piese găsite (pag. ${_identifyPage}/${totalPages})</span>
            <div style="display:flex;gap:6px">`;
        if (_identifyPage > 1) html += `<button class="btn btn-sm id-page-btn" data-pg="${_identifyPage - 1}">◀ Anterior</button>`;
        if (_identifyPage < totalPages) html += `<button class="btn btn-sm id-page-btn" data-pg="${_identifyPage + 1}">Următor ▶</button>`;
        html += `</div></div>`;
        html += `<div style="max-height:calc(100vh - 320px);overflow-y:auto;padding-right:4px">`;
        result.data.forEach((p: PiesaLista) => {
            const wearTxt = p.wear_type === 'U' ? '<span class="badge badge-red">Uzură</span>' : p.wear_type === 'S' ? '<span class="badge badge-yellow">Consumabil</span>' : '';
            const hasDoc = p.document_id && p.document_id > 0;
            const manualBtn = hasDoc
                ? `<a href="#" class="id-manual-btn" data-part-id="${p.id}" style="display:inline-flex;align-items:center;gap:3px;padding:3px 8px;border-radius:6px;background:rgba(0,180,255,.1);border:1px solid rgba(0,180,255,.3);color:#00b4ff;text-decoration:none;font-size:.75rem;font-weight:600;cursor:pointer">📖 Manual</a>`
                : '';
            html += `<div style="padding:10px 12px;margin-bottom:6px;border-radius:10px;background:rgba(255,255,255,.04);border:1px solid var(--border)">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:4px">
                    <div style="font-weight:700;font-size:.9rem"><code style="color:#fb923c">${escHtml(p.part_number)}</code> — ${escHtml(p.description_ro)}</div>
                    <div style="display:flex;gap:6px;flex-shrink:0">${wearTxt}${manualBtn}</div>
                </div>
                <div style="font-size:.8rem;color:var(--text-muted)">Mașină: <strong>${escHtml(p.machine_id)}</strong> | ${escHtml(p.category || '—')} | Interval: ${escHtml(p.interval_text || '—')} | Furnizor: ${escHtml(p.supplier || '—')} | Cant: ${p.quantity_per_machine || 1}</div>
            </div>`;
        });
        html += `</div>`;
        if (totalPages > 1) {
            html += `<div style="display:flex;gap:6px;justify-content:center;margin-top:8px">`;
            if (_identifyPage > 1) html += `<button class="btn btn-sm id-page-btn" data-pg="${_identifyPage - 1}">◀ Anterior</button>`;
            if (_identifyPage < totalPages) html += `<button class="btn btn-sm id-page-btn" data-pg="${_identifyPage + 1}">Următor ▶</button>`;
            html += `</div>`;
        }
        panel.innerHTML = html;
        panel.querySelectorAll('.id-page-btn').forEach(b => {
            b.addEventListener('click', () => _searchIdentifyPart(parseInt((b as HTMLElement).dataset.pg as string)));
        });
        panel.querySelectorAll('.id-manual-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                const el = btn as HTMLElement;
                const orig = el.innerHTML;
                el.innerHTML = '⏳...';
                try {
                    const res = await api('/api/parts/' + el.dataset.partId + '/manual_page');
                    if (res.document_id) {
                        if (res.source === 'nesigur' && res.avertisment) toast(res.avertisment, 'warning');
                        window.open('/api/documents/' + res.document_id + '/view?page=' + (res.page || 1), '_blank');
                    }
                    else toast(res.error || 'Nu s-a găsit manual', 'warning');
                } catch (_e) { toast('Eroare la căutare manual', 'error'); }
                el.innerHTML = orig;
            });
        });
    } catch (err) {
        panel.innerHTML = `<div class="parts-empty-state" style="padding:20px"><p>Eroare: ${escHtml((err as Error).message)}</p></div>`;
    }
}

/* ── Parts Photos Gallery ── */
export async function _loadPartsPhotosGallery() {
    const gallery = document.getElementById('parts-photos-gallery');
    if (!gallery) return;
    try {
        const photos: PozaPiesa[] = await api('/api/parts_photos');
        if (!photos || photos.length === 0) {
            gallery.innerHTML = '<p style="font-size:.85rem;color:var(--text-muted)">Nicio fotografie salvată încă. Fotografiați o piesă pentru a o salva automat aici.</p>';
            return;
        }
        let html = '';
        const photoData: Array<{url: string, caption: string, id: number}> = [];
        const canDelete = state.currentUser?.role === 'master' || state.currentUser?.role === 'engineer';
        photos.forEach((p: PozaPiesa, i: number) => {
            const aiDesc = p.ai_result ? (p.ai_result.description || '') : '';
            const codes = p.ai_result && p.ai_result.part_codes ? p.ai_result.part_codes.join(', ') : '';
            const label = codes || aiDesc || p.filename;
            let caption = '';
            if (aiDesc) caption += `<div style="font-weight:600;margin-bottom:4px">${escHtml(aiDesc)}</div>`;
            if (codes) caption += `<div style="color:#fb923c">Coduri: ${escHtml(codes)}</div>`;
            if (p.machine_id) caption += `<div style="color:var(--cyan)">Mașină: ${escHtml(p.machine_id)}</div>`;
            if (p.created_at) caption += `<div style="font-size:.8rem;color:#94a3b8;margin-top:4px">${escHtml(formatDateTimeRO(p.created_at))}</div>`;
            photoData.push({ url: p.url, caption, id: p.id });
            const delBtn = canDelete
                ? `<button class="photo-delete-btn" data-photo-id="${p.id}" style="position:absolute;top:4px;right:4px;background:rgba(220,38,38,.85);border:none;color:#fff;width:22px;height:22px;border-radius:50%;font-size:.75rem;cursor:pointer;display:none;align-items:center;justify-content:center;z-index:2" title="Șterge fotografia">✕</button>`
                : '';
            html += `<div class="photo-thumb" title="${escHtml(label)}" data-photo-idx="${i}" style="position:relative">
                ${delBtn}
                <img src="${escHtml(p.url)}" alt="${escHtml(label)}" loading="lazy">
                <div class="photo-thumb-label">${escHtml(label.substring(0, 40))}</div>
                <div class="photo-thumb-meta">${escHtml(p.machine_id || '')} · ${escHtml((p.created_at || '').substring(0, 16))}</div>
            </div>`;
        });
        gallery.innerHTML = html;
        if (canDelete) {
            gallery.querySelectorAll('.photo-thumb').forEach(thumb => {
                const delBtn = thumb.querySelector('.photo-delete-btn') as HTMLElement;
                if (delBtn) {
                    thumb.addEventListener('mouseenter', () => { delBtn.style.display = 'flex'; });
                    thumb.addEventListener('mouseleave', () => { delBtn.style.display = 'none'; });
                    delBtn.addEventListener('click', async (ev) => {
                        ev.stopPropagation();
                        const photoId = delBtn.dataset.photoId;
                        if (!await customConfirm('Ștergi această fotografie?\nAcțiunea nu poate fi anulată.', '🗑 Ștergere fotografie')) return;
                        try {
                            await api('/api/parts_photos/' + photoId, { method: 'DELETE' });
                            toast('Fotografia a fost ștearsă');
                            _loadPartsPhotosGallery();
                        } catch (_e) { toast('Eroare la ștergere', 'error'); }
                    });
                }
            });
        }
        gallery.querySelectorAll('.photo-thumb').forEach(thumb => {
            thumb.addEventListener('click', (e) => {
                const idx = parseInt((thumb as HTMLElement).dataset.photoIdx || '0');
                const pd = photoData[idx];
                if (!pd) return;
                if ((e as MouseEvent).shiftKey) {
                    _openLightbox(pd.url, pd.caption);
                    return;
                }
                _showArPreview(pd.url);
                const preview = document.getElementById('ar-preview');
                if (preview) preview.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const panel = document.getElementById('ar-result-panel');
                if (panel && pd.caption) {
                    panel.innerHTML = `<div style="padding:12px;border-radius:10px;background:rgba(255,255,255,.04);border:1px solid var(--border)">
                        <div style="font-size:.8rem;color:var(--text-muted);margin-bottom:6px">Analiza AI anterioară:</div>
                        ${pd.caption}
                        <div style="margin-top:10px;display:flex;gap:8px">
                            <button class="btn btn-sm" id="gallery-reidentify" style="background:var(--cyan);color:#000;font-weight:600">🔄 Re-identifică cu AI</button>
                            <button class="btn btn-sm" id="gallery-enlarge" style="background:rgba(255,255,255,.1)">🔍 Mărește</button>
                        </div>
                    </div>`;
                    document.getElementById('gallery-reidentify')?.addEventListener('click', () => {
                        _identifyPartFromImage(pd.url);
                    });
                    document.getElementById('gallery-enlarge')?.addEventListener('click', () => {
                        _openLightbox(pd.url, pd.caption);
                    });
                }
            });
        });
    } catch (err) {
        gallery.innerHTML = '<p style="color:var(--red)">Eroare la încărcarea galeriei</p>';
    }
}

/* ── Schemes & Desene ── */
export let _schemesPage = 1;
export let _schemesTypeFilter: string | undefined = '';

export async function _loadSchemes(page?: number) {
    const machine = (document.getElementById('schemes-machine') as HTMLInputElement)?.value;
    const searchQ = ((document.getElementById('schemes-search') as HTMLInputElement)?.value || '').trim();
    const panel = document.getElementById('schemes-results');
    if (!panel) return;

    if (!machine && !searchQ) {
        panel.innerHTML = '<div class="parts-empty-state" style="padding:30px"><p>Selectați o mașină sau introduceți un termen de căutare.</p></div>';
        document.getElementById('schemes-type-filter')!.style.display = 'none';
        return;
    }

    if (page) _schemesPage = page;

    panel.innerHTML = '<div style="text-align:center;padding:20px"><div class="typing-dots"><span></span><span></span><span></span></div></div>';

    try {
        const params = new URLSearchParams({ per_page: '20', page: String(_schemesPage) });
        if (machine) params.set('machine_id', machine);
        if (_schemesTypeFilter) params.set('category', _schemesTypeFilter);
        if (searchQ) params.set('q', searchQ);

        const result: { data: ProceduraLista[]; total: number; page: number; per_page: number; type_counts: Record<string, number> }
            = await api(`/api/extract-procedures?${params}`);
        const typeCounts = result.type_counts || {};

        // Type filter buttons
        const typeBar = document.getElementById('schemes-type-filter');
        if (typeBar) {
            const typeLabels = {procedure:'Proceduri',reglaj:'Reglaje',scule:'Scule',instructiune:'Instrucțiuni',image_analysis:'🖼️ Scheme/Desene'};
            const totalAll = Object.values(typeCounts).reduce((a, b) => a + b, 0);
            let btns = `<button class="scheme-type-btn ${!_schemesTypeFilter?'active':''}" data-type="">Toate (${totalAll})</button>`;
            for (const [typ, cnt] of Object.entries(typeCounts)) {
                btns += `<button class="scheme-type-btn ${_schemesTypeFilter===typ?'active':''}" data-type="${typ}">${(typeLabels as Record<string, string>)[typ]||typ} (${cnt})</button>`;
            }
            typeBar.innerHTML = btns;
            typeBar.style.display = 'flex';
            typeBar.querySelectorAll('.scheme-type-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    _schemesTypeFilter = (btn as HTMLElement).dataset.type;
                    _schemesPage = 1;
                    _loadSchemes();
                });
            });
        }

        if (!result.data || result.data.length === 0) {
            panel.innerHTML = `<div class="parts-empty-state" style="padding:20px"><p>Nicio procedură găsită.</p></div>`;
            return;
        }

        let html = `<p style="font-size:.85rem;color:var(--text-muted);margin-bottom:10px">${result.total} rezultate — pagina ${result.page} din ${Math.ceil(result.total/result.per_page)||1}</p>`;

        result.data.forEach((proc: ProceduraLista, idx: number) => {
            const typeIcon = ({procedure:'📋',reglaj:'⚙️',scule:'🔧',instructiune:'📖',image_analysis:'🖼️'} as Record<string, string>)[proc.extract_type] || '📄';
            const typeName = ({procedure:'Procedură',reglaj:'Reglaj',scule:'Sculă/Instrument',instructiune:'Instrucțiune',image_analysis:'Schemă/Desen'} as Record<string, string>)[proc.extract_type] || proc.extract_type;

            const hasDetail = proc.content_ro || proc.steps.length || proc.tools || proc.safety_warnings || proc.reference_values;
            const cardId = `scheme-card-${proc.id}`;

            html += `<div class="scheme-card" id="${cardId}">
                <div class="scheme-card-header" onclick="document.getElementById('${cardId}').classList.toggle('expanded')">
                    <div class="scheme-card-title">
                        <span class="scheme-type-badge">${typeIcon} ${escHtml(typeName)}</span>
                        <h4>${escHtml(proc.title_ro || 'Fără titlu')}</h4>
                    </div>
                    <span class="scheme-expand-icon">▸</span>
                </div>
                <div class="scheme-card-body">`;

            // Image thumbnail for image_analysis type
            if (proc.extract_type === 'image_analysis' && proc.video_ref) {
                html += `<div class="scheme-image-preview" style="margin-bottom:10px">
                    <img src="${escHtml(proc.video_ref)}" alt="Imagine document"
                         style="max-width:100%;max-height:400px;border-radius:6px;cursor:pointer;border:1px solid var(--border)"
                         onclick="event.stopPropagation(); _openLightbox('${escHtml(proc.video_ref)}')">
                </div>`;
            }

            // Warnings at top
            if (proc.safety_warnings) {
                html += `<div class="scheme-warning">⚠️ ${escHtml(proc.safety_warnings)}</div>`;
            }

            // Content or steps
            if (proc.steps.length > 0) {
                html += '<div class="scheme-steps"><ol>';
                proc.steps.forEach((s) => {
                    const cpClass = s.is_checkpoint ? ' class="checkpoint"' : '';
                    html += `<li${cpClass}>${escHtml(s.instruction)}${s.duration_min ? ` <span class="step-dur">(${s.duration_min} min)</span>` : ''}${s.notes ? ` <em class="step-note">${escHtml(s.notes)}</em>` : ''}</li>`;
                });
                html += '</ol></div>';
            } else if (proc.content_ro) {
                html += `<div class="scheme-content">${escHtml(proc.content_ro).replace(/\n/g,'<br>')}</div>`;
            }

            // Meta info row
            let metaParts = [];
            if (proc.tools) metaParts.push(`<span class="scheme-meta-item">🔧 <strong>Scule:</strong> ${escHtml(proc.tools)}</span>`);
            if (proc.reference_values) metaParts.push(`<span class="scheme-meta-item">📏 <strong>Valori referință:</strong> ${escHtml(proc.reference_values)}</span>`);
            if (metaParts.length) {
                html += `<div class="scheme-meta">${metaParts.join('')}</div>`;
            }

            // Action buttons
            let actionsHtml = '<div class="scheme-actions">';
            if (proc.document_id) {
                const pageQuery = proc.start_page != null ? `?page=${proc.start_page + 1}` : '';
                const pageLabel = proc.start_page != null && proc.end_page != null
                    ? ` p.${proc.start_page + 1}–${proc.end_page + 1}`
                    : '';
                const fnameLabel = proc.document_filename ? ' (' + escHtml(proc.document_filename) + pageLabel + ')' : '';
                actionsHtml += `<a href="/api/documents/${proc.document_id}/view${pageQuery}" target="_blank" class="btn btn-sm scheme-doc-btn" data-extract-id="${proc.id}" title="Deschide PDF-ul original la pagina procedurii">📄 Deschide documentul${fnameLabel}</a>`;
            }
            actionsHtml += `<button class="btn btn-sm scheme-proc-btn" onclick="event.stopPropagation(); openProcedureOverlay(${proc.id})" title="Vizualizare procedură interactivă">📋 Procedură interactivă</button>`;
            actionsHtml += '</div>';
            html += actionsHtml;

            html += `</div></div>`;
        });

        panel.innerHTML = html;

        panel.querySelectorAll('.scheme-doc-btn').forEach(btn => {
            const href = btn.getAttribute('href');
            if (href && !href.includes('page=')) {
                btn.addEventListener('click', async function(e) {
                    e.preventDefault();
                    const extId = (btn as HTMLElement).dataset.extractId;
                    if (!extId) { window.open(href, '_blank'); return; }
                    btn.textContent = '⏳ Se caută pagina...';
                    try {
                        const resp = await fetch(`/api/extracts/${extId}/steps/1/pdf_context`);
                        const data = await resp.json();
                        if (data.results && data.results.length > 0) {
                            const best = data.results[0];
                            window.open(`/api/documents/${best.document_id}/view?page=${best.page}`, '_blank');
                        } else {
                            window.open(href, '_blank');
                        }
                    } catch(err) { window.open(href, '_blank'); }
                    btn.textContent = '📄 Deschide documentul';
                });
            }
        });

        // Pagination
        renderPagination('#schemes-pagination', result.total, result.per_page, _schemesPage, (pg: number) => {
            _schemesPage = pg;
            _loadSchemes();
        });

    } catch (err) {
        panel.innerHTML = `<div class="parts-empty-state" style="padding:30px"><p>Eroare la încărcare: ${escHtml((err as Error).message)}</p></div>`;
    }
}

/* ═══ SACMI SPACE INTEGRATION ═════════════════════════════════ */
let _sacmiInited = false;
let _sacmiLinks: RandSacmiLinks[] = [];

export async function initSacmiSpace() {
    if (_sacmiInited) return;
    _sacmiInited = true;

    const machineSel = document.getElementById('sacmi-machine') as HTMLSelectElement;
    if (!machineSel) return;

    try {
        _sacmiLinks = await api('/api/sacmi_links');
        machineSel.innerHTML = '<option value="">Selectați utilajul SACMI...</option>';
        _sacmiLinks.forEach((sl) => {
            machineSel.innerHTML += `<option value="${escHtml(sl.machine_id)}">${escHtml(sl.machine_id)}${sl.catalog_code ? ' ✓' : ''}</option>`;
        });
    } catch {}

    machineSel.addEventListener('change', () => {
        const mid = machineSel.value;
        if (mid) _showSacmiPanel(mid);
        else _resetSacmiPanel();
    });
}

export { initSacmiSpace as initExplorer };

function _resetSacmiPanel() {
    const empty = document.getElementById('sacmi-empty');
    const cards = document.getElementById('sacmi-cards');
    const config = document.getElementById('sacmi-config');
    const status = document.getElementById('sacmi-status');
    if (empty) empty.style.display = '';
    if (cards) { cards.style.display = 'none'; cards.innerHTML = ''; }
    if (config) { config.style.display = 'none'; config.innerHTML = ''; }
    if (status) status.textContent = '';
}

function _showSacmiPanel(machineId: string) {
    const empty = document.getElementById('sacmi-empty');
    const cards = document.getElementById('sacmi-cards');
    const configEl = document.getElementById('sacmi-config');
    const status = document.getElementById('sacmi-status');

    if (empty) empty.style.display = 'none';

    const sl = _sacmiLinks.find((s) => s.machine_id === machineId);
    if (!sl) {
        if (empty) { empty.style.display = ''; }
        return;
    }

    const equipId = sl.equipment_id || '';
    const catalogCode = sl.catalog_code || '';
    const contract = sl.contract || '';

    const _isUrl = (v: string | null) => typeof v === 'string' && v.startsWith('http');
    const esparesUrl = (_isUrl(sl.espares_url) ? sl.espares_url : '') || (catalogCode && equipId
        ? `https://sacmi.space/ESpares/Catalog/Index/${catalogCode}?equipment=${equipId}`
        : '');
    const edocsUrl = (_isUrl(sl.edocs_url) ? sl.edocs_url : '') || (equipId
        ? `https://sacmi.space/EDocs/Docs/List?serialNumber=${encodeURIComponent(equipId)}`
        : 'https://sacmi.space/EDocs/Docs/List');
    const esupportUrl = (_isUrl(sl.esupport_url) ? sl.esupport_url : '') || (equipId
        ? `https://sacmi.space/ESupport?serialNumber=${encodeURIComponent(equipId)}`
        : 'https://sacmi.space/ESupport');

    if (status) {
        status.innerHTML = catalogCode
            ? `<span class="sacmi-status-ok">Configurat</span> — S/N: ${escHtml(equipId)}`
            : `<span class="sacmi-status-warn">Cod catalog lipsă</span> — S/N: ${escHtml(equipId)}`;
    }

    if (cards) {
        cards.style.display = '';
        let html = '<div class="sacmi-cards-grid">';

        html += `<a class="sacmi-card sacmi-card-primary" ${esparesUrl ? `href="${escHtml(esparesUrl)}" target="_blank" rel="noopener"` : ''} ${!esparesUrl ? 'style="opacity:.5;pointer-events:none"' : ''}>
            <div class="sacmi-card-icon">🔍</div>
            <div class="sacmi-card-body">
                <h4>E-Spares</h4>
                <p>Catalog interactiv piese de schimb cu desene explodate, navigare pe nivele și listă piese</p>
            </div>
            <span class="sacmi-card-arrow">→</span>
        </a>`;

        html += `<a class="sacmi-card" href="${escHtml(edocsUrl)}" target="_blank" rel="noopener">
            <div class="sacmi-card-icon">📄</div>
            <div class="sacmi-card-body">
                <h4>E-Docs</h4>
                <p>Documente tehnice: manuale mecanice, electrice, scheme, diagrame</p>
            </div>
            <span class="sacmi-card-arrow">→</span>
        </a>`;

        html += `<a class="sacmi-card" href="${escHtml(esupportUrl)}" target="_blank" rel="noopener">
            <div class="sacmi-card-icon">🛠️</div>
            <div class="sacmi-card-body">
                <h4>E-Support</h4>
                <p>Suport tehnic direct de la SACMI, deschidere tichete, asistență la distanță</p>
            </div>
            <span class="sacmi-card-arrow">→</span>
        </a>`;

        html += `<a class="sacmi-card" href="https://sacmi.space/EPay" target="_blank" rel="noopener">
            <div class="sacmi-card-icon">🛒</div>
            <div class="sacmi-card-body">
                <h4>E-Pay</h4>
                <p>Comandă piese de schimb online direct de la SACMI</p>
            </div>
            <span class="sacmi-card-arrow">→</span>
        </a>`;

        html += '</div>';

        if (!catalogCode) {
            html += `<div class="sacmi-missing-notice">
                <strong>Atenție:</strong> Codul de catalog lipsește pentru acest utilaj. Linkul E-Spares nu este disponibil.
                Accesați <a href="${escHtml(edocsUrl)}" target="_blank">E-Docs</a>, căutați utilajul după S/N <code>${escHtml(equipId)}</code>, apoi configurați codul de catalog mai jos.
            </div>`;
        }

        html += `<div class="sacmi-info-bar">
            <span>S/N: <strong>${escHtml(equipId)}</strong></span>
            ${catalogCode ? `<span>Catalog: <strong>${escHtml(catalogCode)}</strong></span>` : ''}
            ${contract ? `<span>Contract: <strong>${escHtml(contract)}</strong></span>` : ''}
            <span>Cont: <strong>CAP PET PLAST S.R.L.</strong></span>
        </div>`;

        cards.innerHTML = html;
    }

    const userRole = state.currentUser?.role || 'operator';
    if (configEl && (userRole === 'master' || userRole === 'engineer')) {
        configEl.style.display = '';
        const esparesUrlVal = sl.espares_url || '';
        const edocsUrlVal = sl.edocs_url || '';
        const esupportUrlVal = sl.esupport_url || '';
        const notesVal = sl.notes || '';
        configEl.innerHTML = `
            <details class="sacmi-config-details">
                <summary>⚙️ Configurare SACMI Space pentru ${escHtml(machineId)}</summary>
                <div class="sacmi-config-fields">
                    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
                        <label>Cod Catalog (din URL E-Spares)
                            <input type="text" id="sacmi-cfg-catalog" class="dx-input" value="${escHtml(catalogCode)}" placeholder="ex: 002AJ479A">
                        </label>
                        <label>Equipment ID (Serial Number)
                            <input type="text" id="sacmi-cfg-equip" class="dx-input" value="${escHtml(equipId)}" placeholder="ex: 10586373">
                        </label>
                        <label>Nr. Contract
                            <input type="text" id="sacmi-cfg-contract" class="dx-input" value="${escHtml(contract)}" placeholder="ex: MSR022069">
                        </label>
                        <label>Note
                            <input type="text" id="sacmi-cfg-notes" class="dx-input" value="${escHtml(notesVal)}" placeholder="Observații...">
                        </label>
                    </div>
                    <div style="margin-top:12px;border-top:1px solid var(--border);padding-top:12px">
                        <div style="font-size:.85rem;font-weight:600;color:var(--text-muted);margin-bottom:8px">Link-uri personalizate (opțional — lasă gol pentru URL automat)</div>
                        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
                            <label>E-Spares URL
                                <input type="text" id="sacmi-cfg-espares" class="dx-input" value="${escHtml(esparesUrlVal)}" placeholder="https://sacmi.space/ESpares/...">
                            </label>
                            <label>E-Docs URL
                                <input type="text" id="sacmi-cfg-edocs" class="dx-input" value="${escHtml(edocsUrlVal)}" placeholder="https://sacmi.space/EDocs/...">
                            </label>
                            <label>E-Support URL
                                <input type="text" id="sacmi-cfg-esupport" class="dx-input" value="${escHtml(esupportUrlVal)}" placeholder="https://sacmi.space/ESupport/...">
                            </label>
                        </div>
                    </div>
                    <button class="btn btn-primary" id="sacmi-cfg-save" style="margin-top:12px">Salvează</button>
                </div>
            </details>`;

        document.getElementById('sacmi-cfg-save')?.addEventListener('click', async () => {
            const btn = document.getElementById('sacmi-cfg-save') as HTMLButtonElement;
            btn.textContent = 'Se salvează...';
            btn.disabled = true;
            try {
                const _urlOrEmpty = (id: string) => {
                    const v = (document.getElementById(id) as HTMLInputElement).value.trim();
                    return (v && v.startsWith('http')) ? v : '';
                };
                const updated: RandSacmiLinks = await api(`/api/machines/${encodeURIComponent(machineId)}/sacmi_links`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        catalog_code: (document.getElementById('sacmi-cfg-catalog') as HTMLInputElement).value.trim(),
                        equipment_id: (document.getElementById('sacmi-cfg-equip') as HTMLInputElement).value.trim(),
                        contract: (document.getElementById('sacmi-cfg-contract') as HTMLInputElement).value.trim(),
                        notes: (document.getElementById('sacmi-cfg-notes') as HTMLInputElement).value.trim(),
                        espares_url: _urlOrEmpty('sacmi-cfg-espares'),
                        edocs_url: _urlOrEmpty('sacmi-cfg-edocs'),
                        esupport_url: _urlOrEmpty('sacmi-cfg-esupport'),
                    })
                });
                const idx = _sacmiLinks.findIndex((s) => s.machine_id === machineId);
                if (idx >= 0) _sacmiLinks[idx] = updated;
                else _sacmiLinks.push(updated);
                _showSacmiPanel(machineId);
                toast('Configurare SACMI salvată');
            } catch (err) {
                toast('Eroare: ' + (err as Error).message, 'error');
                btn.textContent = 'Salvează';
                btn.disabled = false;
            }
        });
    } else if (configEl) {
        configEl.style.display = 'none';
    }
}


/* ═══ TROUBLESHOOTING ══════════════════════════════════════════ */
export let errorsInited = false;

export async function initErrors() {
    if (!errorsInited) {
        const machines = await api('/api/error_codes/machines');
        const sel = $('#errors-machine');
        sel!.innerHTML = '<option value="">Toate mașinile</option>';
        machines.forEach((m: string) => { sel!.innerHTML += `<option value="${m}">${m}</option>`; });

        $('#errors-btn')!.addEventListener('click', () => { state.errorsPage = 1; loadErrors(); });
        $('#errors-search')!.addEventListener('keydown', e => {
            if (e.key === 'Enter') { state.errorsPage = 1; loadErrors(); }
        });
        document.getElementById('errors-reset')?.addEventListener('click', () => {
            ($('#errors-search') as HTMLInputElement).value = '';
            if ($('#errors-machine')) ($('#errors-machine') as HTMLInputElement).value = '';
            state.errorsPage = 1;
            loadErrors();
        });

        errorsInited = true;
    }
    loadErrors();
}

export async function loadErrors() {
    const q = ($('#errors-search') as HTMLInputElement).value;
    const machine = ($('#errors-machine') as HTMLInputElement).value;
    const params = new URLSearchParams({ page: String(state.errorsPage), per_page: '50' });
    if (q) params.set('q', q);
    if (machine) params.set('machine_id', machine);
    else {
        const linie = (document.getElementById('errors-line') as HTMLSelectElement | null)?.value || '';
        if (linie) params.set('line_id', linie);   // 5 sep 2026, ca la piese
    }

    const result = await api(`/api/error_codes?${params}`);
    $('#errors-info')!.textContent = `${result.total} coduri găsite (pagina ${result.page} din ${Math.ceil(result.total / result.per_page) || 1})`;

    const tbody = $('#errors-table tbody');
    tbody!.innerHTML = '';

    if (result.data.length === 0) {
        tbody!.innerHTML = '<tr><td colspan="7" class="empty-state">Niciun cod de eroare găsit</td></tr>';
    } else {
        result.data.forEach((e: RandErrorCodes) => {
            const sevBadge = e.severity
                ? `<span class="badge badge-${e.severity === 'high' ? 'red' : e.severity === 'medium' ? 'yellow' : 'green'}">${escHtml(e.severity)}</span>`
                : '<span class="badge badge-gray">—</span>';

            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td><strong>${escHtml(e.machine_id)}</strong></td>
                <td><code>${escHtml(e.error_code)}</code></td>
                <td>${escHtml(truncate(e.description_ro, 200))}</td>
                <td>${sevBadge}</td>
                <td>${escHtml(truncate(e.probable_cause, 100))}</td>
                <td>${escHtml(truncate(e.solution, 100))}</td>
                <td>${escHtml(e.parts_needed)}</td>
            `;
            tbody!.appendChild(tr);
        });
    }

    renderPagination('#errors-pagination', result.total, result.per_page, state.errorsPage, (pg: number) => {
        state.errorsPage = pg;
        loadErrors();
    });
}

export async function _initErrorsInDiag() {
    await _ensureMachineCache();
    const machines = await api('/api/error_codes/machines');
    const sel = $('#errors-machine');
    if (sel) {
        sel.innerHTML = '<option value="">Toate mașinile</option>';
        machines.forEach((m: string) => { sel.innerHTML += `<option value="${m}">${m}</option>`; });
        _insertLineSelectBefore(sel, 'errors-line', 'Toate liniile', 'Toate mașinile');
    }
    $('#errors-btn')?.addEventListener('click', () => { state.errorsPage = 1; loadErrors(); });
    $('#errors-search')?.addEventListener('keydown', e => {
        if (e.key === 'Enter') { state.errorsPage = 1; loadErrors(); }
    });
    document.getElementById('errors-reset')?.addEventListener('click', () => {
        const errLine = document.getElementById('errors-line');
        if (errLine) { (errLine as HTMLInputElement).value = ''; errLine.dispatchEvent(new Event('change')); }
        if ($('#errors-search')) ($('#errors-search') as HTMLInputElement).value = '';
        if ($('#errors-machine')) ($('#errors-machine') as HTMLInputElement).value = '';
        state.errorsPage = 1;
        loadErrors();
    });
    loadErrors();
}
